import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {shareReplay} from 'rxjs/operators';

import {ApiLocationService} from '@services/api-location/api-location.service';

import {BucketBrowserPage} from '@models/sidebar/response/bucket-browser-page';

import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {PAGE_CONTAINER_TYPE} from '@constants/sidebar/page-type.constant';
import {LoadingIndicatorService} from '@services/loading-indicator/loading-indicator.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {PageItem} from '@models/sidebar/page-item';
import {NotificationService} from '@services/notification/notification.service';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {BaseMenuItem} from '@models/sidebar/response/base-menu-item';
import {GlobalHelperService} from '@services/global-helper/global-helper.service';

@Injectable({
  providedIn: 'root'
})
export class BucketBrowserPageService {

  private GET_BUCKET_BROWSER_PAGE_URL_SUFFIX = '/api/web/base/:machineId/bucketBrowserPage/:tabId';
  private CREATE_BUCKET_BROWSER_PAGE_URL_SUFFIX = '/api/web/base/:machineId/bucketBrowserPage';
  private DELETE_BUCKET_BROWSER_PAGE_URL_SUFFIX = '/api/web/base/:machineId/bucketBrowserPage/:tabId/baseId/:itemId';
  private GET_BUCKET_BROWSER_PAGE_DETAILS_URL_SUFFIX = '/api/web/base/:machineId/bucketBrowserPage/details/:tabId';
  private RENAME_BUCKET_BROWSER_PAGE_URL_SUFFIX = '/api/web/base/:machineId/bucketBrowserPage/rename/baseId/:itemId';
  private GET_FILE_PATH = '/api/web/storage/:path';

  private readonly savingIndicatorMessage = '';
  private readonly errorCreatingPage = '';
  private readonly successCreatingPage = '';
  private readonly errorRenamingPage = '';
  private readonly successRenamingPage = '';

  private readonly BASE_URL: string;

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService,
              private loadingIndicator: LoadingIndicatorService,
              private leftMenuService: LeftMenuService,
              private notificationService: NotificationService,
              private menuNotificationService: MenuNotificationService,
              private translateService: TranslateService,
              private globalHelper: GlobalHelperService,) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.savingIndicatorMessage = this.translateService.instant('Saving');
    this.errorCreatingPage = this.translateService.instant('Error creating bucket browser page.');
    this.successCreatingPage = this.translateService.instant('Bucket browser page was created.');
    this.errorRenamingPage = this.translateService.instant('Error renaming bucket browser page.');
    this.successRenamingPage = this.translateService.instant('Bucket browser page was renamed.');
  }

  public getBucketBrowserPage(machineId: string, tabId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GET_BUCKET_BROWSER_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':tabId', tabId);

    return this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public create(baseMenuItems: PageItem[], pageItem: PageItem, machineId: string | null, currentPageName: string, parentId: string | null, isAdminOrHigher: boolean) {
    if (machineId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const id = this.leftMenuService.getHighestPageIdNumber(baseMenuItems, pageItem);
      const pageId = PAGE_CONTAINER_TYPE.BUCKET_BROWSER_PAGE + id;
      pageItem.pageId = pageId;
      const bucketBrowserPage = new BucketBrowserPage(pageId, pageItem.name);
      this.createBucketBrowserPage(machineId, bucketBrowserPage).subscribe((newBaseMenuItem: BaseMenuItem) => {
        this.handleCreatedPage(newBaseMenuItem, pageItem, baseMenuItems, currentPageName, parentId, isAdminOrHigher);
      }, (error) => {
        this.notificationService.error(this.errorCreatingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  private createBucketBrowserPage(machineId: string, request: BucketBrowserPage): Observable<any> {
    let url: string = this.BASE_URL + this.CREATE_BUCKET_BROWSER_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    return this.http.post<BucketBrowserPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  private handleCreatedPage(createdItem: BaseMenuItem, pageItem: PageItem, baseMenuItems: PageItem[], currentPageName: string, parentId: string, isAdminOrHigher: boolean) {
    this.menuNotificationService.broadcast(new MenuNotification(OperationType.ADD, parentId, createdItem.name, ''));
    pageItem.id = createdItem.id;
    pageItem.canBeAltered = isAdminOrHigher;
    pageItem.parent = createdItem.parent;
    pageItem.pageId = createdItem.pageId;
    pageItem.index = createdItem.index;
    baseMenuItems.push(pageItem);
    this.loadingIndicator.hide();
    this.notificationService.success(this.successCreatingPage, currentPageName, false);
  }

  public rename(pageItem: PageItem, machineId: string | null, parentId: string | null, currentPageName: string, propertiesFallback: Object): void {
    if (machineId && pageItem.id && pageItem.pageId && parentId) {
      this.loadingIndicator.show(this.savingIndicatorMessage);
      const bucketBrowserTab = new BucketBrowserPage(pageItem.pageId, pageItem.name);
      const itemId: string = pageItem.id;
      this.renameBucketBrowserPage(machineId, itemId, bucketBrowserTab).subscribe((result: BaseMenuItem) => {
        this.globalHelper.handleUpdatedPage(result.name, parentId, currentPageName, itemId, this.successRenamingPage);
      }, (error) => {
        this.leftMenuService.broadcastErrorUpdatePageChange(propertiesFallback);
        this.notificationService.error(this.errorRenamingPage, error, currentPageName, true);
        this.loadingIndicator.hide();
      });
    }
  }

  private renameBucketBrowserPage(machineId: string, itemId: string, request: BucketBrowserPage): Observable<any> {
    let url: string = this.BASE_URL + this.RENAME_BUCKET_BROWSER_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':itemId', itemId);
    return this.http.post<BucketBrowserPage>(url, request, HTTPS_REQUEST_OPTIONS);
  }

  public deleteBucketBrowserPage(machineId: string, tabId: string, itemId: string): Observable<any> {
    let url: string = this.BASE_URL + this.DELETE_BUCKET_BROWSER_PAGE_URL_SUFFIX;
    url = url.replace(':machineId', String(machineId))
    .replace(':tabId', tabId)
    .replace(':itemId', itemId);
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public getBucketBrowserPageDetails(machineId: string, tabId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GET_BUCKET_BROWSER_PAGE_DETAILS_URL_SUFFIX;
    url = url.replace(':machineId', machineId)
    .replace(':tabId', tabId);

    return this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
  }

  public getFileUrl(machineId: string, machineParentId: string, filePath: string): string {
    let path = machineId + '/' + machineParentId;
    if (filePath.startsWith('/')) {
      path += filePath;
    } else {
      path += '/' + filePath;
    }
    let url: string = this.BASE_URL + this.GET_FILE_PATH;
    url = url.replace(':path', path);
    return url;
  }

  public sortItems(items: string[]): void {
    items.sort(function (a, b) {
      return (a).localeCompare(b);
    });
  }

}
